import React, { useState, useEffect, useMemo } from "react";
import ViewStocktakeForm from "./ViewStocktakeForm";
import StartStocktakeSessionForm from "./StartStocktakeSessionForm"
import StocktakeTypeSelectionForm from "./StocktakeTypeSelectionForm"

const StocktakeSearch = ({ refreshTrigger }) => {
  const [sessions, setSessions] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedSession, setSelectedSession] = useState(null);
  const [showView, setShowView] = useState(false);
  const [showStartStocktakeSessionForm, setShowStartStocktakeSessionForm] = useState(false)
  const [inventory, setInventory] = useState([]);
  const [secureData, setSecureData] = useState(null);
  const [loading, setLoading] = useState(false);

  const currentUserId = useMemo(() => {
    const payload = secureData?.admin || secureData?.auth || null;
    return payload?.user_id ?? payload?.admin_id ?? null;
  }, [secureData]);

  const authHeaders = () => {
    const token = localStorage.getItem("token");
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  const fetchSessions = () => {
    setLoading(true);
    fetch("/api/stocktake/sessions", { headers: { ...authHeaders() } })
      .then((r) => {
        if (!r.ok) throw new Error(`Failed to fetch sessions: ${r.status}`);
        return r.json();
      })
      .then((data) => {
        // NOTE: if /api/stocktake/sessions gets migrated to paginated shape
        // ({ items, total, page, limit, totalPages }) like /api/products was,
        // this Array.isArray check will silently return false and sessions
        // will appear empty. Confirm this endpoint's current response shape.
        const rows = Array.isArray(data) ? data : (data.items || []);
        setSessions(rows);
      })
      .catch((e) => console.error("Error fetching stocktakes:", e))
      .finally(() => setLoading(false));
  };

  // API fetchers
  // all=true bypasses pagination so stocktake "By SKU List" mode can see the full inventory
  const fetchProducts = () => {
    fetch("/api/products?all=true", { headers: { ...authHeaders() } })
      .then((r) => {
        if (!r.ok) throw new Error("Failed to fetch products");
        return r.json();
      })
      .then((data) => {
        const rows = Array.isArray(data) ? data : (data.items || []);
        setInventory(rows);
      })
      .catch((e) => console.error("Error fetching products:", e));
  };

  // ✅ Refresh User List After Adding
  const handleSessionAdded = () => {
    fetchSessions();
    setShowStartStocktakeSessionForm(false);
  };

  const handleView = async (sessionId) => {
    try {
      const [sessionRes, itemsRes] = await Promise.all([
        fetch(`/api/stocktake/sessions/${sessionId}`, { headers: { ...authHeaders() } }),
        fetch(`/api/stocktake/sessions/${sessionId}/lines`, { headers: { ...authHeaders() } }),
      ]);

      if (!sessionRes.ok) throw new Error(`Failed to fetch session: ${sessionRes.status}`);
      if (!itemsRes.ok) throw new Error(`Failed to fetch session lines: ${itemsRes.status}`);

      const session = await sessionRes.json();
      const items = await itemsRes.json();
      setSelectedSession({ ...session, items: Array.isArray(items) ? items : (items.items || []) });
      setShowView(true);
    } catch (e) {
      console.error("Error fetching session details:", e);
    }
  };

  useEffect(() => {
    fetchSessions();
    fetchProducts();
  }, [refreshTrigger]);

  const filteredSessions = sessions.filter((s) => {
    const q = searchQuery.toLowerCase();
    return (
      (s.name || "").toLowerCase().includes(q) ||
      (s.location_name || "").toLowerCase().includes(q) ||
      (s.status || "").toLowerCase().includes(q) ||
      (s.scope || "").toLowerCase().includes(q) ||
      String(s.session_id || "").includes(q)
    );
  });

  const handleAddSession = () => {
    setShowStartStocktakeSessionForm(true);
  };

  return (
    <div className="user-list-container">
      <h2>Stock-Take Sessions</h2>

      <div>
        <label>Search:</label>
        <input
          type="text"
          placeholder="Search by name, location, status…"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
        />
      </div>
      <div className="button-container">
        <button className="add-user-button" onClick={handleAddSession}>Start Stocktake Session</button>
      </div>

      <div className="UserList-container">
        <table>
          <thead>
            <tr>
              <th className="first-child">ID</th>
              <th>Session Name</th>
              <th>Status</th>
              <th>Started</th>
              <th>Ended</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan="6">Loading…</td>
              </tr>
            ) : filteredSessions.length > 0 ? (
              filteredSessions.map((s) => (
                <tr key={s.session_id}>
                  <td className="first-child">{s.session_id}</td>
                  <td>{s.name}</td>
                  <td>{s.status}</td>
                  <td className="date-cell">
                    {s.started_at ? (
                      <>
                        {new Date(s.started_at).toLocaleDateString('en-GB')}
                        <time>{new Date(s.started_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</time>
                      </>
                    ) : "-"}
                  </td>

                  <td className="date-cell">
                    {s.ended_at ? (
                      <>
                        {new Date(s.ended_at).toLocaleDateString('en-GB')}
                        <time>{new Date(s.ended_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</time>
                      </>
                    ) : "-"}
                  </td>
                  <td>
                    <button onClick={() => handleView(s.session_id)}>View</button>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="6">No stock-take sessions match your search.</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      {showStartStocktakeSessionForm && (
        <StocktakeTypeSelectionForm
          onClose={() => setShowStartStocktakeSessionForm(false)}
          inventory={inventory}
          fetchProducts={fetchProducts}
          userId={currentUserId}
          onSessionAdded={fetchSessions}
        />
      )}

      {showView && selectedSession && (
        <ViewStocktakeForm
          selectedSession={selectedSession}
          onClose={() => setShowView(false)}
        />
      )}
    </div>
  );
};

export default StocktakeSearch;